<template>
  <PageTemplate>
    <Loader v-if="!isLoaded"/>
    <div v-else>
      <div class="d-flex justify-content-between align-items-center">
        <h6 class="element-header">
          Kunden
        </h6>

        <Buttons/>
      </div>

      <div class="element-box">
        <Menu/>

        <Search
            :finishUnemployedDropdown="finishUnemployedDropdown"
            :aaNumberDropdown="aaNumberDropdown"
            :officeDropdown="officeDropdown"
            :filters="filters"
            :loading="isSearching"
            :insuranceNumberDropdown="insuranceNumberDropdown"
            @search="getUnemployedList"
            @dropdown-fetch="handleDropdownSearch"
            @clear-dropdown="handleClearDropdown"/>

        <div class="row align-items-center justify-content-between mt-3">
          <div class="col-auto">
            <PerPage
                :values="[10, 25, 50, 100]"
                @update:perPage="getUnemployedList"/>

            <ExcelExportButton
                style="margin-right: 15px;"
                :label="$t('general.excel_export')"
                :loading="isExcelLoaded"
                @click="getExcelExport"/>
          </div>
        </div>

        <div class="table-responsive overflow-auto mt-4">
          <table class="table table-striped">
            <thead>
            <tr>
              <th>Kundennumer</th>
              <th>Status</th>
              <th>Vorname</th>
              <th>Nachname</th>
              <th>Standort</th>
              <th>Telefonnummer</th>
              <th>Erstellt am Tag</th>
              <th style="text-align: right; padding-right: 30px; min-width: 150px;">
                {{ $t('general.actions') }}
              </th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(unemployed) in unemployedList.data" :key="unemployed.id">
              <td>{{ unemployed?.customerNumber }}</td>
              <td>{{ unemployed?.statusTranslated }}</td>
              <td>{{ unemployed?.firstName }}</td>
              <td>{{ unemployed?.lastName }}</td>
              <td>{{ unemployed?.location?.name }}</td>
              <td>{{ unemployed?.phone }}</td>
              <td>{{ unemployed?.createdAt }}</td>
              <td class="text-end">
                <button class="btn btn-primary"
                        @click="router.push({
                        name: 'unemployedPrivateCoachingList',
                         params: { id: unemployed?.id }
                        })">
                  <i class="os-icon os-icon-edit"></i>
                </button>
                <button class="btn btn-danger ms-2" @click="deleteModal(unemployed?.id)">
                  <i class="os-icon os-icon-trash"></i>
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
        <Pagination
            v-if="unemployedList?.pagination?.total_pages > 1"
            :totalPages="unemployedList?.pagination?.total_pages"
            :currentPage="unemployedList?.pagination?.current_page"
            :totalItems="unemployedList?.pagination?.total_items"
            :perPage="unemployedList?.pagination?.items_per_page"
            @pagechanged="getUnemployedList"/>
      </div>

      <ModalDelete
          v-model="showModalDelete"
          :header="t('general.delete_item_header')"
          :onDelete="deleteUnemployed"/>

    </div>
  </PageTemplate>
</template>

<script setup>
import unemployedService from '@/api/Unemployed';
import Loader from '@/components/Loader.vue';
import ModalDelete from '@/components/modal/ModalDelete.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import Pagination from '@/components/Pagination.vue';
import PerPage from '@/components/PerPage.vue';
import {CloseToast, ErrorToast, SuccessToast} from '@/components/VueToast';
import router from '@/router';
import {getFinishedUnemployedDropdown, getOfficeDropdown} from '@/shared/dropdowns';
import {downloadFile} from '@/shared/functions';
import Buttons from '@/views/App/Unemployed/List/Buttons.vue';
import Search from '@/views/App/Unemployed/List/Finished/Search.vue';
import Menu from '@/views/App/Unemployed/List/Menu.vue';
import {onMounted, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import ExcelExportButton from "@/components/ExcelExportButton.vue";

const {t} = useI18n();

const unemployedList = ref([]);
const finishUnemployedDropdown = ref([]);
const officeDropdown = ref([]);
const aaNumberDropdown = ref([]);
const insuranceNumberDropdown = ref([]);
const showModalDelete = ref(false);
const unemployedId = ref(null);
const isExcelLoaded = ref(false);

const filters = ref({
  status: '',
  insuranceNumber: '',
  aaNumber: '',
  lastName: '',
  locationId: '',
});

const isSearching = ref(false);
const isLoaded = ref(false);

async function getUnemployedList(page = 1, perPage, passedFilters) {
  CloseToast();

  if (passedFilters) {
    isSearching.value = true;
  }

  const perPageRes = unemployedList.value?.pagination?.items_per_page;
  try {
    const {data} = await unemployedService.getUnemployedFinished({
      page: page,
      limit: perPage ?? perPageRes,
      ...(passedFilters ? passedFilters : filters.value),
    });
    unemployedList.value = data;
    isSearching.value = false;
  } catch (error) {
    await ErrorToast(error);
    isSearching.value = false;
  }
}

function deleteModal(id) {
  unemployedId.value = null;
  CloseToast();
  showModalDelete.value = true;
  unemployedId.value = id;
}

async function deleteUnemployed() {
  try {
    filters.value = {
      status: '',
      interestedIn: '',
      lastName: '',
      contactPersonId: '',
    };

    showModalDelete.value = false;

    await unemployedService.deleteUnemployed(unemployedId.value);
    await getUnemployedList();
    SuccessToast(t('general.toasts.delete_success'));
  } catch (error) {
    await ErrorToast(error);
  }
}


const handleDropdownSearch = async (searchTerm, type) => {
  if (searchTerm.length < 3) return; //

  const filters = {
    searchTerm: searchTerm,
    searchTermFor: type
  }

  if (type === 'lastName') await getFinishedUnemployedDropdown(
      finishUnemployedDropdown,
      'lastName',
      'lastName',
      filters
  )

  if (type === 'aaNumber') await getFinishedUnemployedDropdown(
      aaNumberDropdown,
      'aaNumber',
      'aaNumber',
      filters
  )

  if (type === 'insuranceNumber') await getFinishedUnemployedDropdown(
      insuranceNumberDropdown,
      'insuranceNumber',
      'insuranceNumber',
      filters
  )
};

const handleClearDropdown = (type) => {
  if (type === 'lastName') finishUnemployedDropdown.value = [];
  if (type === 'aaNumber') aaNumberDropdown.value = []
  if (type === 'insuranceNumber') insuranceNumberDropdown.value = []
}

async function getExcelExport() {
  await downloadFile('/unemployed/finished-export', undefined,
      filters.value,
      false,
      "GET",
      isExcelLoaded
  )
}

onMounted(async () => {
  await Promise.all([
    getUnemployedList(),
    getOfficeDropdown(officeDropdown),
  ]);
  isLoaded.value = true;
});

</script>

<style scoped>
.nav.sm {
  width: max-content;
}
</style>y